import React, { useEffect, useState } from 'react';
import { reportsApi, farmApi, boxApi } from '../services/api';
import { LivestockReport, Farm, Box } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError } from '../utils/handleApiError';

interface FinancialReport {
  period: { from: string; to: string };
  revenue: {
    total_revenue: number;
    sales_count: number;
    animals_sold: number;
    revenue_per_animal: number;
  };
  costs: {
    total_costs: number;
    cost_per_animal: number;
    by_category: Record<string, number>;
  };
  profit: {
    gross_profit: number;
    profit_margin: number;
    profit_per_animal: number;
  };
}

interface HealthReport {
  period: { from: string; to: string };
  treatments: {
    total_treatments: number;
    animals_treated: number;
    vaccinations: number;
    vet_approval_rate: number;
  };
  treatments_by_medicine: Record<string, number>;
  active_withdrawals: Array<{
    animal: { id: number; ear_tag: string };
    farm: string;
    withdrawal_end_date: string;
    medicine: string;
  }>;
}

interface WithdrawalCalendar {
  period_days: number;
  calendar: Record<string, Array<{
    animal: { id: number; ear_tag: string; estimated_weight: number };
    farm: string;
    box: string;
    treatment: {
      medicine: string;
      treatment_date: string;
      withdrawal_days: number;
    };
  }>>;
  summary: {
    total_animals: number;
    total_withdrawals: number;
  };
}

const Reports: React.FC = () => {
  const { t, refreshTranslations } = useLanguage();
  const [livestockReport, setLivestockReport] = useState<LivestockReport | null>(null);
  const [financialReport, setFinancialReport] = useState<FinancialReport | null>(null);
  const [healthReport, setHealthReport] = useState<HealthReport | null>(null);
  const [withdrawalCalendar, setWithdrawalCalendar] = useState<WithdrawalCalendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState<'livestock' | 'financial' | 'health' | 'withdrawal'>('livestock');

  // Filter states
  const [farms, setFarms] = useState<Farm[]>([]);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState<number | undefined>(undefined);
  const [selectedBoxId, setSelectedBoxId] = useState<number | undefined>(undefined);
  const [fromDate, setFromDate] = useState<string>(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadFarms();
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFarmId) {
      loadBoxes(selectedFarmId);
    } else {
      setBoxes([]);
      setSelectedBoxId(undefined);
    }
  }, [selectedFarmId]);

  const loadFarms = async () => {
    try {
      const farmsData = await farmApi.getAll();
      setFarms(farmsData);
    } catch (error) {
      console.error('Error loading farms:', error);
      handleApiError(error, 'Hiba a telephelyek betöltésekor');
    }
  };

  const loadBoxes = async (farmId: number) => {
    try {
      const boxesData = await boxApi.getAll(farmId);
      setBoxes(boxesData);
    } catch (error) {
      console.error('Error loading boxes:', error);
      handleApiError(error, 'Hiba a boxok betöltésekor');
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      const params = {
        farm_id: selectedFarmId,
        box_id: selectedBoxId,
        from_date: fromDate,
        to_date: toDate,
      };

      const [livestock, financial, health, withdrawal] = await Promise.all([
        reportsApi.getLivestockReport(params),
        reportsApi.getFinancialReport(params),
        reportsApi.getHealthReport(params),
        reportsApi.getWithdrawalCalendar({ ...params, days: 30 })
      ]);
      setLivestockReport(livestock);
      setFinancialReport(financial);
      setHealthReport(health);
      setWithdrawalCalendar(withdrawal);
    } catch (error) {
      console.error('Error loading reports:', error);
      handleApiError(error, 'Hiba a riportok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    loadReports();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('reports_management', 'reports')}</h1>
          <p className="mt-2 text-gray-600">
            {t('reports_management_desc', 'reports')}
          </p>
        </div>
        <button
          onClick={() => refreshTranslations?.()}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
        >
          Fordítások frissítése
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('filters', 'reports')}
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* From Date */}
            <div>
              <label htmlFor="from-date" className="block text-sm font-medium text-gray-700 mb-1">
                {t('from_date', 'reports')}
              </label>
              <input
                type="date"
                id="from-date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* To Date */}
            <div>
              <label htmlFor="to-date" className="block text-sm font-medium text-gray-700 mb-1">
                {t('to_date', 'reports')}
              </label>
              <input
                type="date"
                id="to-date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Farm Filter */}
            <div>
              <label htmlFor="farm-filter" className="block text-sm font-medium text-gray-700 mb-1">
                {t('farm', 'reports')}
              </label>
              <select
                id="farm-filter"
                value={selectedFarmId || ''}
                onChange={(e) => setSelectedFarmId(e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('all_farms', 'reports')}</option>
                {farms.map((farm) => (
                  <option key={farm.id} value={farm.id}>
                    {farm.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Box Filter */}
            <div>
              <label htmlFor="box-filter" className="block text-sm font-medium text-gray-700 mb-1">
                {t('box', 'reports')}
              </label>
              <select
                id="box-filter"
                value={selectedBoxId || ''}
                onChange={(e) => setSelectedBoxId(e.target.value ? Number(e.target.value) : undefined)}
                disabled={!selectedFarmId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t('all_boxes', 'reports')}</option>
                {boxes.map((box) => (
                  <option key={box.id} value={box.id}>
                    {box.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Apply Button */}
            <div className="flex items-end">
              <button
                onClick={handleFilterChange}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
              >
                {t('apply_filters', 'reports')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Navigation */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('report_types', 'reports')}
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => setActiveReport('livestock')}
              className={`p-4 rounded-lg text-left transition-colors ${
                activeReport === 'livestock'
                  ? 'bg-blue-100 border-2 border-blue-500'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <span className="ml-2 font-medium text-gray-900">{t('livestock_report', 'reports')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('livestock_report_desc', 'reports')}</p>
            </button>

            <button
              onClick={() => setActiveReport('financial')}
              className={`p-4 rounded-lg text-left transition-colors ${
                activeReport === 'financial'
                  ? 'bg-green-100 border-2 border-green-500'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="ml-2 font-medium text-gray-900">{t('financial_report', 'reports')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('financial_report_desc', 'reports')}</p>
            </button>

            <button
              onClick={() => setActiveReport('health')}
              className={`p-4 rounded-lg text-left transition-colors ${
                activeReport === 'health'
                  ? 'bg-red-100 border-2 border-red-500'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <span className="ml-2 font-medium text-gray-900">{t('health_report', 'reports')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('health_report_desc', 'reports')}</p>
            </button>

            <button
              onClick={() => setActiveReport('withdrawal')}
              className={`p-4 rounded-lg text-left transition-colors ${
                activeReport === 'withdrawal'
                  ? 'bg-yellow-100 border-2 border-yellow-500'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="ml-2 font-medium text-gray-900">{t('withdrawal_calendar', 'reports')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('withdrawal_calendar_desc', 'reports')}</p>
            </button>
          </div>
        </div>
      </div>

      {/* Livestock Report */}
      {activeReport === 'livestock' && livestockReport && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('livestock_report', 'reports')}
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('total_animals', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{livestockReport.totals.total_animals ?? 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('active_animals', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{livestockReport.totals.active_animals}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('average_weight', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {livestockReport.weight_stats.average_weight
                        ? `${livestockReport.weight_stats.average_weight}kg`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('sold_animals', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{livestockReport.totals.sold_animals}</p>
                  </div>
                </div>
              </div>
            </div>

            {livestockReport.age_distribution && Object.keys(livestockReport.age_distribution).length > 0 && (
              <div className="mt-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('age_distribution', 'reports')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(livestockReport.age_distribution).map(([ageGroup, count]) => (
                    <div key={ageGroup} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">{ageGroup}</span>
                        <span className="text-lg font-bold text-gray-900">{count} {t('pieces', 'general')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Financial Report */}
      {activeReport === 'financial' && financialReport && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('financial_report', 'reports')}
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="text-lg font-medium text-green-900 mb-4">{t('revenue', 'reports')}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-green-700">{t('total_revenue', 'reports')}</span>
                    <span className="font-medium text-green-900">{financialReport.revenue.total_revenue.toLocaleString()} Ft</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-green-700">{t('sales_count', 'reports')}</span>
                    <span className="font-medium text-green-900">{financialReport.revenue.sales_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-green-700">{t('animals_sold', 'reports')}</span>
                    <span className="font-medium text-green-900">{financialReport.revenue.animals_sold}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-green-700">{t('revenue_per_animal', 'reports')}</span>
                    <span className="font-medium text-green-900">{financialReport.revenue.revenue_per_animal.toLocaleString()} Ft</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 p-6 rounded-lg">
                <h4 className="text-lg font-medium text-red-900 mb-4">{t('costs', 'reports')}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-red-700">{t('total_costs', 'reports')}</span>
                    <span className="font-medium text-red-900">{financialReport.costs.total_costs.toLocaleString()} Ft</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-red-700">{t('cost_per_animal', 'reports')}</span>
                    <span className="font-medium text-red-900">{financialReport.costs.cost_per_animal.toLocaleString()} Ft</span>
                  </div>
                </div>
                {Object.keys(financialReport.costs.by_category).length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-red-900 mb-2">{t('by_category', 'reports')}</h5>
                    <div className="space-y-1">
                      {Object.entries(financialReport.costs.by_category).map(([category, amount]) => (
                        <div key={category} className="flex justify-between text-xs">
                          <span className="text-red-700">{category}</span>
                          <span className="text-red-900">{Number(amount).toLocaleString()} Ft</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="text-lg font-medium text-blue-900 mb-4">{t('profit', 'reports')}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-700">{t('gross_profit', 'reports')}</span>
                    <span className="font-medium text-blue-900">{financialReport.profit.gross_profit.toLocaleString()} Ft</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-700">{t('profit_margin', 'reports')}</span>
                    <span className="font-medium text-blue-900">{financialReport.profit.profit_margin}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-700">{t('profit_per_animal', 'reports')}</span>
                    <span className="font-medium text-blue-900">{financialReport.profit.profit_per_animal.toLocaleString()} Ft</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Report */}
      {activeReport === 'health' && healthReport && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('health_report', 'reports')}
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('total_treatments', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{healthReport.treatments.total_treatments}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('animals_treated', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{healthReport.treatments.animals_treated}</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('vaccinations', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{healthReport.treatments.vaccinations}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('vet_approval_rate', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{healthReport.treatments.vet_approval_rate}%</p>
                  </div>
                </div>
              </div>
            </div>

            {Object.keys(healthReport.treatments_by_medicine).length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('treatments_by_medicine', 'reports')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(healthReport.treatments_by_medicine).slice(0, 9).map(([medicine, count]) => (
                    <div key={medicine} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">{medicine}</span>
                        <span className="text-lg font-bold text-gray-900">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {healthReport.active_withdrawals.length > 0 && (
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('active_withdrawals', 'reports')}</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('animal', 'reports')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('farm', 'reports')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('medicine', 'reports')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('withdrawal_end_date', 'reports')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {healthReport.active_withdrawals.slice(0, 10).map((withdrawal, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {withdrawal.animal.ear_tag}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {withdrawal.farm}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {withdrawal.medicine}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(withdrawal.withdrawal_end_date).toLocaleDateString('hu-HU')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Withdrawal Calendar */}
      {activeReport === 'withdrawal' && withdrawalCalendar && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('withdrawal_calendar', 'reports')}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {t('next_days', 'reports')}: {withdrawalCalendar.period_days}
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('total_animals', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{withdrawalCalendar.summary.total_animals}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('total_withdrawals', 'reports')}</p>
                    <p className="text-2xl font-bold text-gray-900">{withdrawalCalendar.summary.total_withdrawals}</p>
                  </div>
                </div>
              </div>
            </div>

            {Object.keys(withdrawalCalendar.calendar).length > 0 && (
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('withdrawal_schedule', 'reports')}</h4>
                <div className="space-y-4">
                  {Object.entries(withdrawalCalendar.calendar).map(([date, withdrawals]) => (
                    <div key={date} className="border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-2">
                        {new Date(date).toLocaleDateString('hu-HU')}
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {withdrawals.map((withdrawal, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">
                                {withdrawal.animal.ear_tag} ({withdrawal.animal.estimated_weight}kg)
                              </div>
                              <div className="text-gray-600">
                                {withdrawal.farm} - {withdrawal.box}
                              </div>
                              <div className="text-gray-600">
                                {withdrawal.treatment.medicine}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;